import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useConfigStore } from 'state/store/config/useConfigStore';

interface SignupRouteGuardProps {
    children: React.ReactNode;
}

/**
 * Route guard component that blocks access to signup-related routes
 * when emailSignUpEnabled is false or undefined in configuration settings.
 * 
 * Blocked routes:
 * - /signup
 * - /signup-email-sent
 * 
 * When access is blocked, users are redirected to /login
 */
export const SignupRouteGuard: React.FC<SignupRouteGuardProps> = ({ children }) => {
    const { configurationSettings } = useConfigStore();
    const location = useLocation();

    // List of signup-related routes that should be blocked
    const blockedRoutes = ['/signup', '/signup-email-sent'];

    // Check if current route should be blocked
    const isBlockedRoute = blockedRoutes.some(route =>
        location.pathname === route || location.pathname.startsWith(`${route}/`)
    );

    // Get the emailSignUpEnabled setting from configuration
    const isEmailSignUpEnabled = configurationSettings?.paths?.emailSignUpEnabled ?? false;

    // If emailSignUpEnabled is false and user is trying to access a blocked route
    if (!isEmailSignUpEnabled && isBlockedRoute) {
        // Redirect to login page
        return <Navigate to="/login" replace />;
    }

    // Allow access to the route
    return <>{children}</>;
};
